import axios from 'axios';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { AUTH_REQUEST_TIMEOUT_MS } from '@/lib/apiTimeout';
import { attachCorrelationErrorLogging } from '@/lib/axiosCorrelationLog';

// API base URL - Use Next.js API proxy to avoid CORS issues
// In production, NEXT_PUBLIC_API_URL must be set
const API_BASE_URL = getApiBaseUrl();

// Safe error message for production: never expose stack traces or internal details
function getSafeAuthMessage(
  error: unknown,
  defaultMessage: string
): string {
  const axiosError = error && typeof error === 'object' && 'response' in error
    ? error as { response?: { status?: number; data?: Record<string, unknown> }; message?: string }
    : null;
  const data = axiosError?.response?.data;
  const raw =
    (typeof data === 'object' && data !== null && typeof data.message === 'string' ? data.message : null)
    ?? (typeof data === 'object' && data !== null && typeof data.error === 'string' ? data.error : null)
    ?? (typeof data === 'object' && data !== null && typeof data.detail === 'string' ? data.detail : null)
    ?? (typeof data === 'object' && data !== null && typeof data.msg === 'string' ? data.msg : null)
    ?? axiosError?.message
    ?? (error instanceof Error ? error.message : undefined);
  if (typeof raw !== 'string' || raw.length > 256) return defaultMessage;
  if (/stack|at\s+\S+\s+\(|Error:\s*[A-Z][a-z]+Error/i.test(raw)) return defaultMessage;
  return raw;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: AUTH_REQUEST_TIMEOUT_MS,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

attachCorrelationErrorLogging(api, 'auth');

export interface EmployeeInfo {
  id: number | string;
  empCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  designation?: string;
  role?: string;
  status?: string;
}

export interface EmployeeLoginResult {
  success: boolean;
  accessToken: string;
  refreshToken?: string;
  sessionId?: string;
  employee: EmployeeInfo;
}

/** Response when 2FA is not set up yet — user must complete setup-2fa flow */
export interface LoginRequires2FASetup {
  kind: 'requires2FASetup';
  tempToken: string;
  expiresIn: number;
  message?: string;
}

/** Response when 2FA is enabled — user must enter TOTP or backup code */
export interface LoginRequires2FA {
  kind: 'requires2FA';
  tempToken: string;
  expiresIn: number;
}

/** Response when login completes with tokens (legacy or if backend skips 2FA) */
export interface LoginSuccess {
  kind: 'success';
  accessToken: string;
  refreshToken?: string;
  sessionId?: string;
  employee: EmployeeInfo;
}

export type EmployeeLoginResponse =
  | LoginRequires2FASetup
  | LoginRequires2FA
  | LoginSuccess;

// Employee login: POST /auth/employee/login
// Returns either 2FA required (setup or verify) or direct success with tokens
export const employeeLogin = async (username: string, password: string): Promise<EmployeeLoginResponse> => {
  try {
    const response = await api.post<{
      requires2FASetup?: boolean;
      requires2FA?: boolean;
      tempToken?: string;
      expiresIn?: number;
      message?: string;
      accessToken?: string;
      refreshToken?: string;
      sessionId?: string;
      employee?: EmployeeInfo;
    }>('/auth/employee/login', { username, password }, {
      headers: { 'Content-Type': 'application/json' }
    });

    const data = response.data || {};

    if (data.requires2FASetup && data.tempToken) {
      return {
        kind: 'requires2FASetup',
        tempToken: data.tempToken,
        expiresIn: typeof data.expiresIn === 'number' ? data.expiresIn : 600,
        message: data.message
      };
    }

    if (data.requires2FA && data.tempToken) {
      return {
        kind: 'requires2FA',
        tempToken: data.tempToken,
        expiresIn: typeof data.expiresIn === 'number' ? data.expiresIn : 600
      };
    }

    if (data.accessToken && data.employee) {
      return {
        kind: 'success',
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        sessionId: data.sessionId,
        employee: data.employee
      };
    }

    throw new Error('Invalid login response from server');
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Login failed. Please try again.'));
  }
};

// POST /auth/employee/setup-2fa — get TOTP secret, QR code, and backup codes
// Sends temp token in body (tempToken + temp_token) and optionally as Bearer for backend compatibility
export const setup2FA = async (tempToken: string): Promise<{
  secret: string;
  qrCode: string;
  backupCodes: string[];
}> => {
  try {
    const response = await api.post<{ secret: string; qrCode: string; backupCodes: string[] }>(
      '/auth/employee/setup-2fa',
      { tempToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const data = response.data;
    if (!data?.secret || !data?.qrCode || !Array.isArray(data.backupCodes)) {
      throw new Error('Invalid setup 2FA response');
    }
    return { secret: data.secret, qrCode: data.qrCode, backupCodes: data.backupCodes };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, '2FA setup failed. Please try again.'));
  }
};

// POST /auth/employee/verify-2fa-setup — verify TOTP during setup, enable 2FA, return tokens
export const verify2FASetup = async (
  tempToken: string,
  secret: string,
  totpCode: string
): Promise<EmployeeLoginResult> => {
  try {
    const response = await api.post<{
      accessToken: string;
      refreshToken?: string;
      sessionId?: string;
      employee: EmployeeInfo;
    }>('/auth/employee/verify-2fa-setup', { tempToken, secret, totpCode }, {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = response.data;
    if (!data?.accessToken || !data?.employee) {
      throw new Error('Invalid verify 2FA setup response');
    }
    return {
      success: true,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      sessionId: data.sessionId,
      employee: data.employee
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Verification failed. Check the code and try again.'));
  }
};

// POST /auth/employee/verify-2fa — verify TOTP or backup code, return tokens
export const verify2FA = async (
  tempToken: string,
  totpCode: string
): Promise<EmployeeLoginResult> => {
  try {
    const response = await api.post<{
      accessToken: string;
      refreshToken?: string;
      sessionId?: string;
      employee: EmployeeInfo;
    }>('/auth/employee/verify-2fa', { tempToken, totpCode }, {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = response.data;
    if (!data?.accessToken || !data?.employee) {
      throw new Error('Invalid verify 2FA response');
    }
    return {
      success: true,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      sessionId: data.sessionId,
      employee: data.employee
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Invalid code. Please try again.'));
  }
};

// POST /auth/employee/setup-password — first-time password setup
export const employeeSetupPassword = async (payload: {
  employeeId: number | string;
  username: string;
  password: string;
  confirmPassword: string;
}) => {
  try {
    const response = await api.post<{ message?: string; success?: boolean }>('/auth/employee/setup-password', payload);
    return {
      success: true,
      message: response.data?.message || 'Password setup completed successfully'
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Password setup failed. Please try again.'));
  }
};

// POST /auth/employee/forgot-password — request password reset email
export const employeeForgotPassword = async (payload: { username?: string; email?: string }) => {
  try {
    const response = await api.post<{ message?: string; success?: boolean }>('/auth/employee/forgot-password', payload);
    return {
      success: true,
      message: response.data?.message || 'If the account exists, a password reset email has been sent.'
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Request failed. Please try again.'));
  }
};

// POST /auth/employee/reset-password — set new password using token from email
export const employeeResetPassword = async (payload: { token: string; password: string; confirmPassword: string }) => {
  try {
    const response = await api.post<{ message?: string; success?: boolean }>('/auth/employee/reset-password', payload);
    return {
      success: true,
      message: response.data?.message || 'Password reset successfully'
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Password reset failed. Please use a fresh link from your email.'));
  }
};

// POST /auth/employee/change-password — change password when authenticated
export const employeeChangePassword = async (
  token: string,
  payload: { currentPassword: string; newPassword: string; confirmPassword: string }
) => {
  try {
    const response = await api.post<{ message?: string; success?: boolean }>('/auth/employee/change-password', payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return {
      success: true,
      message: response.data?.message || 'Password changed successfully'
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Password change failed. Please check your current password and try again.'));
  }
};

// GET /auth/employee/check-setup/{employeeId} — check if employee has set password
export const employeeCheckSetup = async (employeeId: number | string): Promise<{ success: boolean; isPasswordSet?: boolean; hasCredentials?: boolean }> => {
  try {
    const response = await api.get<{ isPasswordSet?: boolean; hasCredentials?: boolean }>(`/auth/employee/check-setup/${Number(employeeId)}`);
    return {
      success: true,
      isPasswordSet: response.data?.isPasswordSet,
      hasCredentials: response.data?.hasCredentials
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Failed to check setup status.'));
  }
};

// Refresh token
export const refreshToken = async (refreshToken: string) => {
  try {
    const response = await api.post('/auth/refresh', {
      refreshToken: refreshToken
    });
    return {
      success: true,
      accessToken: response.data.accessToken || response.data.token,
      refreshToken: response.data.refreshToken
    };
  } catch (error: unknown) {
    throw new Error(getSafeAuthMessage(error, 'Session expired. Please log in again.'));
  }
};
